/**
 * Lazy Load Composable
 * 
 * Loads content when a Bootstrap component (collapse, modal, offcanvas) is shown.
 * Prevents loading heavy components until needed.
 * 
 * @param {Controller} controller - The Stimulus controller instance
 * @param {Object} options - Configuration options
 * @param {string} options.event - Bootstrap event to listen for ('show.bs.collapse', 'show.bs.modal', 'show.bs.offcanvas')
 * @param {Function} options.load - Function to call when event fires (receives the event)
 * @param {boolean} options.once - Only load once (default: true)
 * 
 * @example Lazy load on collapse show
 * import { useLazyLoad } from "../utils/use_lazy_load"
 * 
 * export default class extends Controller {
 *   static values = { url: String }
 *   
 *   connect() {
 *     useLazyLoad(this, {
 *       event: 'show.bs.collapse',
 *       load: () => this.fetchContent()
 *     })
 *   }
 *   
 *   async fetchContent() {
 *     const response = await fetch(this.urlValue)
 *     // ... handle response
 *   }
 * }
 * 
 * @example Lazy load Turbo Frame on modal show
 * import { useLazyLoad } from "../utils/use_lazy_load"
 * 
 * export default class extends Controller {
 *   static targets = ['frame']
 *   static values = { src: String }
 *   
 *   connect() {
 *     useLazyLoad(this, {
 *       event: 'show.bs.modal',
 *       load: () => {
 *         if (this.hasFrameTarget && this.hasSrcValue) {
 *           this.frameTarget.src = this.srcValue
 *         }
 *       }
 *     })
 *   }
 * }
 */
export function useLazyLoad(controller, options = {}) {
  const { event, load, once = true } = options
  
  if (!event || !load) {
    console.warn('useLazyLoad: event and load options are required')
    return
  }

  let loaded = false
  
  const handleEvent = (e) => {
    if (once && loaded) return
    loaded = true
    load(e)
  }

  // Add event listener
  controller.element.addEventListener(event, handleEvent)

  // Store original disconnect to chain with cleanup
  const originalDisconnect = controller.disconnect?.bind(controller)

  // Override disconnect to clean up event listener
  controller.disconnect = () => {
    controller.element.removeEventListener(event, handleEvent)
    
    if (originalDisconnect) {
      originalDisconnect()
    }
  }

  // Return control methods
  return {
    load: handleEvent,
    reset: () => { loaded = false }
  }
}

/**
 * Helper to lazy-load a Turbo Frame's src attribute
 * 
 * @param {HTMLElement} frame - The turbo-frame element
 * @param {string} src - The URL to load
 * @param {Object} options - Configuration options
 * @param {boolean} options.skipIfLoaded - Skip if already has src (default: true)
 */
export function loadTurboFrame(frame, src, options = {}) {
  const { skipIfLoaded = true } = options
  
  if (!frame || !src) return false
  
  // Skip if already loaded
  if (skipIfLoaded && frame.getAttribute('src')) {
    return false
  }
  
  frame.setAttribute('src', src)
  return true
}

/**
 * Helper to fetch HTML content and insert it into a container
 * 
 * @param {string} url - URL to fetch
 * @param {HTMLElement} container - Container to insert content into
 * @param {Object} options - Configuration options
 * @param {HTMLElement} options.placeholder - Placeholder element to hide after load
 * @param {string} options.errorMessage - Error message to show on failure
 * @returns {Promise<boolean>} - Whether the load was successful
 */
export async function fetchHtmlContent(url, container, options = {}) {
  const { placeholder, errorMessage = 'Failed to load. Please refresh the page.' } = options
  
  if (!url || !container) return false

  try {
    const response = await fetch(url, {
      headers: {
        'Accept': 'text/html',
        'X-Requested-With': 'XMLHttpRequest'
      }
    })

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`)
    }

    const html = await response.text()
    container.innerHTML = html

    if (placeholder) {
      placeholder.classList.add('d-none')
    }

    return true
  } catch (error) {
    console.error('Failed to load content:', error)

    if (placeholder) {
      placeholder.innerHTML = `
        <div class="text-danger">
          <i class="fa fa-exclamation-triangle me-2"></i>
          ${errorMessage}
        </div>
      `
    }

    return false
  }
}

